import { useEffect, useRef } from 'react'
import {
  PASSWORD_MAX_UTF8_BYTES,
  PASSWORD_MIN_CHARACTERS,
  PASSWORD_MIN_SCORE,
  passwordAssessment,
  scorePassword,
} from '../lib/passwordPolicy'
import type { PasswordAssessment } from '../lib/passwordPolicy'

interface PasswordStrengthIndicatorProps {
  password: string
  confirmation?: string
  confirmationTouched?: boolean
  active: boolean
  describedById: string
  onAssessment: (assessment: PasswordAssessment) => void
}

export default function PasswordStrengthIndicator({
  password,
  confirmation,
  confirmationTouched = false,
  active,
  describedById,
  onAssessment,
}: PasswordStrengthIndicatorProps) {
  const generationRef = useRef(0)
  const currentRef = useRef(passwordAssessment('', 0, 'neutral'))

  useEffect(() => {
    const generation = ++generationRef.current
    let cancelled = false
    const initial = passwordAssessment(
      password,
      generation,
      active && password ? 'pending' : 'neutral',
    )
    currentRef.current = initial
    onAssessment(initial)

    if (!active || !password) return () => { cancelled = true }

    const timer = window.setTimeout(() => {
      void scorePassword(password).then((score) => {
        if (cancelled || generation !== generationRef.current) return
        const settled = passwordAssessment(password, generation, 'settled', score)
        currentRef.current = settled
        onAssessment(settled)
      }).catch(() => {
        if (cancelled || generation !== generationRef.current) return
        const settled = passwordAssessment(password, generation, 'settled', 0)
        currentRef.current = settled
        onAssessment(settled)
      })
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [active, onAssessment, password])

  const assessment = currentRef.current.value === password
    ? currentRef.current
    : passwordAssessment(password, generationRef.current, password ? 'pending' : 'neutral')
  const settled = assessment.status === 'settled'
  const confirmationMatches = confirmation !== undefined && password === confirmation
  const settledSummary = settled
    ? assessment.accepted
      ? `${assessment.label}. Password meets the policy.`
      : `${assessment.label}. Password does not yet meet the policy.`
    : ''

  return (
    <div id={describedById} className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
      <p className="text-slate-700">
        {assessment.characterCount} {assessment.characterCount === 1 ? 'character' : 'characters'} ·{' '}
        {assessment.byteCount} / {PASSWORD_MAX_UTF8_BYTES} UTF-8 bytes
      </p>
      <p className="text-xs text-slate-500">Some Unicode characters use multiple UTF-8 bytes.</p>
      {assessment.status === 'pending' && <p className="font-medium text-slate-600">Checking strength…</p>}
      <meter
        min={0}
        max={4}
        value={assessment.score ?? 0}
        aria-label="Password strength"
        aria-valuetext={settled ? assessment.label ?? undefined : 'Not yet scored'}
        className="h-2 w-full"
      />
      <p className="font-medium text-slate-700">{settled ? assessment.label : 'Not yet scored'}</p>
      <ul className="space-y-1 text-xs text-slate-600">
        <li>{assessment.characterCount >= PASSWORD_MIN_CHARACTERS ? '✓' : '○'} At least {PASSWORD_MIN_CHARACTERS} characters</li>
        <li>{assessment.byteCount <= PASSWORD_MAX_UTF8_BYTES ? '✓' : '○'} At most {PASSWORD_MAX_UTF8_BYTES} UTF-8 bytes</li>
        <li>{settled && (assessment.score ?? 0) >= PASSWORD_MIN_SCORE ? '✓' : '○'} Strong password or passphrase</li>
        {confirmation !== undefined && (
          <li>{confirmationMatches ? '✓' : '○'} Confirmation matches
            {confirmationTouched && !confirmationMatches ? ' — passwords do not match' : ''}
          </li>
        )}
      </ul>
      <span className="sr-only" aria-live="polite">{settledSummary}</span>
    </div>
  )
}
